import { useMemo } from "react";
import { Chart, getColorFromCssVariable } from "ui";
import { ChartCard } from "./ChartCard";
import { useDatadogQuery } from "./useDatadogQuery";
import { aggregateBrandData, percentageFormatter } from "./utils";

type BrandDistributionData = {
  label: string;
  value: number;
}[];

export function BrandDistributionChart() {
  const { ref, queryStatus, data, error, refetch } =
    useDatadogQuery<BrandDistributionData>({
      chartType: "BIN_BRANDS",
      delayMs: 2400,
    });

  const { labels, brandDistributionData } = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0)
      return { labels: [], brandDistributionData: [] };

    const aggregatedData = aggregateBrandData(data);
    const sortedData = aggregatedData.sort((a, b) => b.value - a.value);
    const total = sortedData.reduce((sum, item) => sum + item.value, 0);

    return {
      labels: sortedData.map((d) => d.label),
      brandDistributionData: sortedData.map((d) =>
        total > 0 ? (d.value / total) * 100 : 0
      ),
    };
  }, [data]);

  return (
    <ChartCard
      ref={ref}
      title="Brand Distribution"
      description="Share of payments by card brand"
      contentClassName="items-center justify-center h-auto min-h-80"
      queryStatus={queryStatus}
      error={error}
      retry={refetch}
    >
      <Chart
        type="doughnut"
        labels={labels}
        datasets={[
          {
            data: brandDistributionData,
            backgroundColor: [
              getColorFromCssVariable("--chart-categorical-6-subtler"),
              getColorFromCssVariable("--chart-categorical-7-subtler"),
              getColorFromCssVariable("--chart-categorical-5-subtler"),
              getColorFromCssVariable("--chart-categorical-4-subtler"),
              getColorFromCssVariable("--chart-categorical-1-subtler"),
              getColorFromCssVariable("--chart-categorical-2-subtler"),
              getColorFromCssVariable("--chart-categorical-3-subtler"),
              getColorFromCssVariable("--chart-categorical-8-subtler"),
              getColorFromCssVariable("--background-info-bold"),
              getColorFromCssVariable("--background-warning-bold"),
            ],
            borderColor: getColorFromCssVariable(
              "--border-foundations-subtler"
            ),
            hoverBorderColor: getColorFromCssVariable(
              "--border-foundations-subtle"
            ),
            borderWidth: 2,
          },
        ]}
        className="flex flex-col items-center"
      >
        <Chart.Legend
          labelFormatter={(label, index) => (
            <>
              <span className="capitalize">{label} </span>
              <span className="text-foundations-subtle">
                ({percentageFormatter(brandDistributionData[index])}%)
              </span>
            </>
          )}
        />
        <Chart.Canvas className="size-56" />
        <Chart.Tooltip
          variant="circle"
          valueFormatter={(value) => <>({percentageFormatter(value)}%)</>}
        />
      </Chart>
    </ChartCard>
  );
}
